import { Injectable } from "@angular/core";
import type { BrowserAgent } from "@newrelic/browser-agent/loaders/browser-agent";
import { ConsentService } from "./consent.service";
import { AgentOptions } from "@newrelic/browser-agent/src/loaders/agent";

type NewRelicCustomAttributeKeys = "practice_id" | "device_id" | "site_id" | "domain" | "path" | "patient_id";

type NewRelicCustomAttributes = Partial<Record<NewRelicCustomAttributeKeys, string | undefined>>;

@Injectable({ providedIn: "root" })
export class NewRelicAgentFactory {
  create(BrowserAgentClass: typeof BrowserAgent, config: AgentOptions): BrowserAgent {
    return new BrowserAgentClass(config);
  }
}

@Injectable({ providedIn: "root" })
export class NewRelicService {
  private _browserAgent: BrowserAgent;
  private _initialized = false;

  constructor(private _agentFactory: NewRelicAgentFactory, private _consentService: ConsentService) {
    /**
     * Proxy object for the browser agent
     * Ensures:
     * 1. Any method call before init() is a no-op
     * 2. Future methods on BrowserAgent automatically handled
     */
    this._browserAgent = new Proxy({} as BrowserAgent, {
      get: (target, prop, receiver) => {
        if (prop in target) {
          return Reflect.get(target, prop, receiver);
        }

        const handler = (...args: unknown[]) => {
          if (this._initialized) {
            const method = this._browserAgent[prop];
            if (typeof method === "function") {
              return method.call(this._browserAgent, ...args);
            }
          }
        };

        // Cache the handler on the target
        Object.defineProperty(target, prop, {
          value: handler,
          configurable: true,
          writable: true,
          enumerable: false,
        });

        return Reflect.get(target, prop, receiver);
      },
    });
  }

  public setUserId(userId: string) {
    this._browserAgent.setUserId(userId);
  }

  public setCustomAttribute(name: string, value: string, persist = false) {
    this._browserAgent.setCustomAttribute(name, value, persist);
  }

  public setCustomAttributes(attrs: NewRelicCustomAttributes) {
    Object.entries(attrs)
      .filter(([_, v]) => v !== undefined)
      .forEach(([k, v]) => this.setCustomAttribute(k, v));
  }

  public async init(metaData: NewRelicCustomAttributes): Promise<void> {
    if (this._initialized || !this._consentService.performanceAllowed()) return;
    this._initialized = true;

    // Dynamically import BrowserAgent to ensure we dont load unnecessary code into the bundle when we don't need it
    const module = await import("@newrelic/browser-agent/loaders/browser-agent");

    const options = {
      init: {
        session_replay: {
          enabled: true,
          autoStart: false,
          block_selector: "",
          mask_text_selector: "*",
          mask_all_inputs: true,
          collect_fonts: true,
          inline_images: false,
          inline_stylesheet: true,
          fix_stylesheets: true,
          preload: false,
          mask_input_options: {},
        },
        distributed_tracing: { enabled: true },
        privacy: { cookies_enabled: true },
        ajax: { deny_list: ["bam.eu01.nr-data.net"] },
      },
      info: { beacon: "bam.eu01.nr-data.net", errorBeacon: "bam.eu01.nr-data.net", licenseKey: "NRJS-2685bb1c797511cfeb9", applicationID: "538575980", sa: 1 },
      loader_config: { accountID: "3034779", trustKey: "2582370", agentID: "538575980", licenseKey: "NRJS-2685bb1c797511cfeb9", applicationID: "538575980" },
    } as AgentOptions;

    this._browserAgent = this._agentFactory.create(module.BrowserAgent, options);

    this.setCustomAttributes(metaData);
  }

  public get browserAgent(): BrowserAgent {
    return this._browserAgent;
  }
}
